import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import MonomerMappingForm, {MonomerMapping} from 'bulk-import/panes/MonomerMappingForm';
import {RegistryLabelingStrategy} from 'constants/RegistryConstants';
import {RegistrationTableInsertType} from 'notebook/integrations/registry/RegistrationTableConstants';
import {INVALID_MONOMER_MAPPING_CONFIG} from 'notebook/structured-tables/bases-columns/BasesColumnConstants';
import StructuredTableSettingsModal, {
  StructuredTableFolderSettingsConfig,
} from 'notebook/structured-tables/components/modals/StructuredTableSettingsModal';
import {RowOnlyConfig} from 'notebook/structured-tables/components/RowByRowSubmissionSetting';
import {StructuredTableFolderConfig} from 'notebook/structured-tables/components/useSelectFolderOptionForTable';
import SchemaLabelingStrategyInput from 'registry/labeling-strategies/SchemaLabelingStrategyInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {RunModalProps} from 'ui-library/runModal';

interface RegistrationTableSettingsModalProps extends RunModalProps<RegistrationTableSettingsModalValues> {
  locked: boolean;
  rowOnlyConfig?: RowOnlyConfig;
  labelingStrategy: RegistryLabelingStrategy | null;
  monomerMapping?: MonomerMapping | typeof INVALID_MONOMER_MAPPING_CONFIG | null;
  insertType?: RegistrationTableInsertType;
  folderConfig?: StructuredTableFolderConfig;
  showMonomerMappingForm?: boolean;
  tagSchemaId: string;
}

export interface RegistrationTableSettingsModalValues {
  labelingStrategy: RegistryLabelingStrategy | null;
  monomerMapping?: MonomerMapping | null;
  folderConfig?: StructuredTableFolderConfig;
  rowByRowSubmissionRequired?: boolean;
}

export default function RegistrationTableSettingsModal(
  props: RegistrationTableSettingsModalProps
): JSX.Element {
  const [labelingStrategy, setLabelingStrategy] = useState<RegistryLabelingStrategy | null>(
    props.labelingStrategy
  );
  const [monomerMapping, setMonomerMapping] = useState(
    props.monomerMapping !== INVALID_MONOMER_MAPPING_CONFIG ? props.monomerMapping : null
  );

  const folderSettings: StructuredTableFolderSettingsConfig | undefined =
    props.folderConfig != null
      ? {
          initialFolderConfig: props.folderConfig,
          itemTypeSupportsFolders: true,
          isForCreate: props.insertType === RegistrationTableInsertType.CREATE_NEW_ENTITIES,
          projectIsRequired: BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_REGISTRY_FILES,
        }
      : undefined;

  return (
    <StructuredTableSettingsModal
      close={props.close}
      locked={props.locked}
      rowOnlyConfig={props.rowOnlyConfig}
      folderSettings={folderSettings}
      onSave={(sharedValues) => {
        props.close({
          ...sharedValues,
          labelingStrategy,
          monomerMapping,
        });
      }}
    >
      <SchemaLabelingStrategyInput
        disabled={props.locked}
        labelingStrategy={labelingStrategy}
        tagSchemaId={props.tagSchemaId}
        onChange={(newLabelingStrategy: RegistryLabelingStrategy) => {
          setLabelingStrategy(newLabelingStrategy);
        }}
      />
      {props.showMonomerMappingForm && (
        <MonomerMappingForm
          onSetMonomerMapping={setMonomerMapping}
          monomerMapping={monomerMapping}
          disabled={props.locked}
          sectionHeader={<FieldLabel label="Nucleotide interpretation settings" />}
        />
      )}
    </StructuredTableSettingsModal>
  );
}
